/**
 * ProjectOfficialView — `/projects/$projectId/official`
 *
 * Tab "Documentos oficiales" del proyecto. Alineada al prototipo
 * (Pantalla Official Browser):
 *  - Header con counter total + link a Lifecycle.
 *  - Filtros: search por name/code + toggle include_superseded.
 *  - Sections collapsibles por phase, en orden del lifecycle:
 *    - Phases con oficiales: tabla con artifacts.
 *    - Phases en curso: nota de "X en proceso → Lifecycle".
 *    - Phases skipped/client_provided-pendiente: header dashed sin tabla.
 *    - Phases sin oficiales: header colapsado por default.
 *
 * Datos:
 *  - GET /projects/{id}/lifecycle → phases ordenadas (para conocer el
 *    universo total de phases del proyecto, no solo las que tienen
 *    oficiales).
 *  - GET /projects/{id}/canon?include_superseded= → artifacts oficiales
 *    agrupados por su fase (`artifacts.phase_id`). Backend path conserva
 *    `/canon` (API contract); frontend lo expone como "official".
 *
 * El backend ya agrupa los oficiales por phase_code (`phase-2`, etc.). Este
 * orchestrator merge phases del lifecycle con groups del response — phases
 * sin group todavía aparecen como sections vacías.
 */

import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation, Trans } from 'react-i18next'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Search } from 'lucide-react'

import { ApiError } from '../../api/client'
import {
  fetchProjectOfficial,
  fetchProjectLifecycle,
  type OfficialArtifact,
  type ProjectPhase,
} from '../../api/projects'
import { useAccessToken } from '../../auth/useAccessToken'
import { OfficialPhaseSection } from '../../components/projects/OfficialPhaseSection'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { useUrlSearchInput } from '../../hooks/useUrlSearchInput'
import { useProjectContext } from './ProjectShell'

export function ProjectOfficialView() {
  const { t } = useTranslation(['projects', 'errors'])
  const { project } = useProjectContext()
  const { accessToken } = useAccessToken()

  // Filtros persisten en URL para que "Volver a documentos oficiales"
  // desde el viewer/editor conserve el contexto (search + toggle de
  // superseded). El input mantiene state local + debounce 300ms al URL
  // para no inflar el historial por keystroke.
  const navigate = useNavigate({ from: '/projects/$projectId/official' })
  const searchParams = useSearch({ from: '/projects/$projectId/official' })
  const includeSuperseded: boolean = searchParams.superseded === true
  const commitSearch = useCallback(
    (q: string) =>
      void navigate({
        search: (prev) => {
          const next = { ...prev }
          if (q.trim() === '') delete next.q
          else next.q = q
          return next
        },
        replace: true,
      }),
    [navigate],
  )
  const [searchInput, setSearchInput] = useUrlSearchInput(
    searchParams.q ?? '',
    commitSearch,
  )
  const search = searchParams.q ?? ''
  const setIncludeSuperseded = (next: boolean) =>
    void navigate({
      search: (prev) => {
        const out = { ...prev }
        if (next) out.superseded = true
        else delete out.superseded
        return out
      },
      replace: true,
    })

  // Lifecycle phases (universo completo) — para mostrar todas las phases
  // del proyecto incluso sin oficiales todavía.
  const lifecycleQuery = useQuery({
    queryKey: ['projects', project.id, 'lifecycle'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchProjectLifecycle(accessToken, project.id)
    },
    enabled: !!accessToken,
    staleTime: 30_000,
    retry: false,
  })

  // Documentos oficiales agrupados por phase_code.
  const officialQuery = useQuery({
    queryKey: [
      'projects',
      project.id,
      'official',
      includeSuperseded ? 'with-superseded' : 'official-only',
    ],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchProjectOfficial(accessToken, project.id, {
        include_superseded: includeSuperseded,
      })
    },
    enabled: !!accessToken,
    staleTime: 30_000,
    retry: false,
  })

  const phases = lifecycleQuery.data?.phases ?? []
  const officialGroups = useMemo(() => officialQuery.data?.groups ?? [], [officialQuery.data])
  const totalOfficial = officialQuery.data?.total_artifacts ?? 0

  // Mapa phase_code → artifacts (post-filter por search).
  const filteredArtifactsByPhase = useMemo(() => {
    const q = search.trim().toLowerCase()
    const map = new Map<string, OfficialArtifact[]>()
    for (const group of officialGroups) {
      const filtered = q
        ? group.artifacts.filter(
            (a) =>
              a.name.toLowerCase().includes(q) ||
              a.code?.toLowerCase().includes(q) ||
              a.template_key.toLowerCase().includes(q),
          )
        : group.artifacts
      map.set(group.phase_code, filtered)
    }
    return map
  }, [officialGroups, search])

  const shownCount = useMemo(() => {
    let n = 0
    for (const arts of filteredArtifactsByPhase.values()) n += arts.length
    return n
  }, [filteredArtifactsByPhase])

  // ─── Render ───────────────────────────────────────────────────────────────
  if (lifecycleQuery.isLoading || officialQuery.isLoading) {
    return (
      <ScrollPage padding="tight">
        <p className="font-mono text-xs text-ink-subtle">
          {t('official.loading')}
        </p>
      </ScrollPage>
    )
  }
  if (lifecycleQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={lifecycleQuery.error} />
      </ScrollPage>
    )
  }
  if (officialQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={officialQuery.error} />
      </ScrollPage>
    )
  }

  // Si el proyecto no tiene phases materializadas (legacy sin migración
  // 0008 aplicada), reusamos el patrón del Lifecycle: empty state honesto.
  if (phases.length === 0) {
    return (
      <ScrollPage padding="tight">
        <GlobalEmptyState />
      </ScrollPage>
    )
  }

  const orderedPhases = [...phases].sort((a, b) => a.phase_order - b.phase_order)

  return (
    <ScrollPage padding="tight">
      <div className="max-w-7xl">
      {/* Header */}
      <header className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle mb-1">
            {t('official.header_eyebrow')}
          </p>
          <h2 className="font-display text-2xl font-semibold text-ink">
            {t('official.header_title')}
          </h2>
          <p className="text-sm text-ink-muted font-body mt-1 max-w-prose">
            {totalOfficial === 0 ? (
              t('official.header_subtitle_zero')
            ) : (
              <Trans
                i18nKey="official.header_subtitle"
                t={t}
                values={{ count: totalOfficial }}
                components={{
                  0: (
                    <Link
                      to="/projects/$projectId/lifecycle"
                      params={{ projectId: project.id }}
                      className="text-brand hover:underline"
                    />
                  ),
                }}
              />
            )}
          </p>
        </div>
        {/* Documentos oficiales son read-only — no se crean aquí. Crear
            artifacts vive en el Lifecycle drill-in (donde están los drafts
            en proceso). Doc 06: oficial = inventario inmutable
            post-aprobación. */}
      </header>

      {/* Filtros */}
      <div className="bg-surface border border-line rounded-lg p-3 mb-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-260px max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('official.filters.search_placeholder')}
            className="w-full bg-canvas border border-line rounded-md pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:border-brand"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-ink-muted cursor-pointer">
          <input
            type="checkbox"
            checked={includeSuperseded}
            onChange={(e) => setIncludeSuperseded(e.target.checked)}
            className="accent-brand"
          />
          {t('official.filters.include_superseded_label')}
        </label>
        <span className="ml-auto text-xs text-ink-subtle">
          {t('official.filters.showing', { shown: shownCount, total: totalOfficial })}
        </span>
      </div>

      {/* Sections por phase */}
      {totalOfficial === 0 && search === '' ? (
        <GlobalEmptyState />
      ) : (
        orderedPhases.map((phase) => (
          <OfficialPhaseSection
            key={phase.id}
            phase={phase}
            artifacts={filteredArtifactsByPhase.get(phase.phase_code) ?? []}
            inFlightCount={computeInFlightCount(phase)}
            projectId={project.id}
            filteredOut={
              search !== '' &&
              (filteredArtifactsByPhase.get(phase.phase_code)?.length ?? 0) === 0
            }
          />
        ))
      )}
      </div>
    </ScrollPage>
  )
}

// ─── Empty state ────────────────────────────────────────────────────────────
function GlobalEmptyState() {
  const { t } = useTranslation('projects')
  return (
    <div className="max-w-2xl mx-auto rounded-md border border-dashed border-line bg-surface p-10 text-center">
      <h3 className="font-display text-lg text-ink">
        {t('official.empty_global_title')}
      </h3>
      <p className="mt-2 font-body text-sm text-ink-muted">
        {t('official.empty_global_body')}
      </p>
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
/** Cuenta artifacts de la phase que NO son oficiales todavía (drafts +
 * in_review). Usado para mostrar "X en proceso — verlos en Lifecycle"
 * en phases activas. */
function computeInFlightCount(phase: ProjectPhase): number {
  const c = phase.artifact_counts
  return (c.draft ?? 0) + (c.in_review ?? 0)
}

function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation(['projects', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-mono text-xs text-critical">
      {t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })}
    </div>
  )
}
